import { Component, EventEmitter, Input, Output } from '@angular/core';

import { Observable, Subject, Subscription, combineLatest } from 'rxjs';

@Component({
	selector: 'checkbox-list',
	templateUrl: 'checkbox-list.html',
	styleUrls: ['checkbox-list.css']
})
export class CheckboxListComponent<T> {
	// Id du champ
	@Input() id!: string;

	// Label pour l'option wildcard (dont la valeur vaut 'null')
	@Input() wildcardOptionLabel: string | null = 'Tous';
	
	// Liste des items presentes
	items: T[] = [];
	
	@Input() optionAllChecked: boolean = false;
	optionsChecked: boolean[] = [];
	@Input() set itemList(list: T[]) {
		this.items = [...list];
		this.itemListChanges.next();
	}
	private itemListChanges = new Subject<void>();
	
	// Fonction permettant d'obtenir le label d'un item
	@Input() labelGetter!: (item: T) => string;
	
	// Fonction permettant d'obtenir la couleur du label d'un item
	@Input() labelColorGetter: (item: T) => string | null = (_item: T) => null;
	
	// Fonction permettant d'obtenir le title d'un item (optionel)
	@Input() titleGetter: (item: T) => string | null = (_item: T) => null;
	
	// Fonction indiquant si l'item doit etre preselectionne ou pas
	private isItemPreselected: (item: T) => boolean = (_item: T) => false;
	@Input() set preselectionFunction(lambda :(item: T) => boolean) {
		this.isItemPreselected = lambda;
		this.preselectionFunctionChanges.next();
	}
	private preselectionFunctionChanges = new Subject<void>();
	
	// Observable pour les signaux de reinitialisation des valeurs par defaut
	private resetSelectionSubscription!: Subscription;
	@Input() set resetCommands(commands: Observable<void> | undefined) {
		if (this.resetSelectionSubscription) {
			this.resetSelectionSubscription.unsubscribe();
		}
		if (commands) {
			this.resetSelectionSubscription = commands.subscribe(
				() => this.resetSelectionCommands.next()
			);
		}
	}
	private resetSelectionCommands = new Subject<void>();
	
	// Observable indiquant des evenements de selection commandes par le parent
	private selectionRequestsSubscription!: Subscription;
	@Input() set selectionRequests(obs: Observable<(item: T) => boolean>) {
		if (this.selectionRequestsSubscription) {
			this.selectionRequestsSubscription.unsubscribe();
		}
		this.selectionRequestsSubscription = obs.subscribe(
			(selectionFunction) => {
				let somethingWasSelected = false;
				this.items.forEach(
					(item, index) => {
						const isDeselected = !this.optionsChecked[index];
						if (isDeselected) {
							const toSelect = selectionFunction(item);
							if (toSelect) {
								this.optionsChecked[index] = true;
								this.itemSelected.emit(item);
								somethingWasSelected = true;
							}
						}
					}
				);
				if (somethingWasSelected) {
					const selectedItems = this.items.filter((_item: T, index: number) => this.optionsChecked[index]);
					const allChecked = this.optionsChecked.findIndex((checked: boolean) => !checked) === -1;
					this.optionAllChecked = allChecked;
					this.selectedItemsChange.emit([selectedItems, allChecked]);
				}
			}
		);
	}
	
	// Observable indiquant des evenements de deselection commandes par le parent
	private deselectionRequestsSubscription!: Subscription;
	@Input() set deselectionRequests(obs: Observable<(item: T) => boolean>) {
		if (this.deselectionRequestsSubscription) {
			this.deselectionRequestsSubscription.unsubscribe();
		}
		this.deselectionRequestsSubscription = obs.subscribe(
			(deselectionFunction) => {
				let somethingWasDeselected = false;
				this.items.forEach(
					(item, index) => {
						const isSelected = this.optionsChecked[index];
						if (isSelected) {
							const toDeselect = deselectionFunction(item);
							if (toDeselect) {
								this.optionsChecked[index] = false;
								this.optionAllChecked = false;
								this.itemDeselected.emit(item);
								somethingWasDeselected = true;
							}
						}
					}
				);
				if (somethingWasDeselected) {
					const selectedItems = this.items.filter((_item: T, index: number) => this.optionsChecked[index]);
					this.selectedItemsChange.emit([selectedItems, false]);
				}
			}
		);
	}
	
	// Sortie pour les valeurs selectionnees
	@Output() itemSelected = new EventEmitter<T>();
	@Output() itemDeselected = new EventEmitter<T>();
	@Output() selectedItemsChange = new EventEmitter<[T[], boolean]>();

	constructor( //
	) {
		// Mise en place des conditions provoquant le reset de la selection
		combineLatest([this.itemListChanges, this.preselectionFunctionChanges, this.resetSelectionCommands]).subscribe(
			(_signal: [void, void, void]) => this.doPreselection()
		);
		
		// Quoiqu'il arrive, a la (re)construction du composant, il faut signaler qu'on au moins la fonction de pre-selection par defaut
		this.preselectionFunctionChanges.next();
		
		// ... et commander l'initialisation de depart, que le parent en demande une ou non
		this.resetSelectionCommands.next();
	}
	
	private doPreselection() {
		this.optionsChecked = new Array(this.items.length);
		let selectedItems = [];
		for (let i = 0; i < this.items.length; i++) {
			const item = this.items[i];
			const selected = this.isItemPreselected(item);
			this.optionsChecked[i] = selected;
			if (selected) {
				selectedItems.push(item);
			}
		}
		const allChecked = (this.optionsChecked.findIndex((checked: boolean) => !checked) === -1);
		this.optionAllChecked = allChecked;
		this.selectedItemsChange.emit([selectedItems, allChecked]);
	}

	onOptionAllChange() {
		const checked = this.optionAllChecked;
		const changed = this.items.filter(
			(_item, index) => this.optionsChecked[index] != checked
		);
		this.optionsChecked.fill(checked);
		if (checked) {
			this.selectedItemsChange.emit([[...this.items], true]);
			changed.forEach(
				(itemChanged) => this.itemSelected.emit(itemChanged)
			)
		} else {
			this.selectedItemsChange.emit([[], false]);
			changed.forEach(
				(itemChanged) => this.itemDeselected.emit(itemChanged)
			)
		}
	}
	
	onOptionChange(index: number) {
		const item = this.items[index];
		const checked = this.optionsChecked[index];
		if (checked) {
			this.itemSelected.emit(item);
			const allChecked = this.optionsChecked.findIndex((checked: boolean) => !checked) === -1;
			if (allChecked) {
				this.optionAllChecked = true;
				this.selectedItemsChange.emit([[...this.items], true]);
				return;
			}
		} else {
			this.itemDeselected.emit(item);
			this.optionAllChecked = false;
		}
		
		const selectedItems = this.items.filter((_item: T, index: number) => this.optionsChecked[index]);
		this.selectedItemsChange.emit([selectedItems, false]);
	}
}